/**
 * Legacy builds, your side (the wealth endgame): what you've given (a tower, a hospital, a university, a stadium, a
 * data centre, one of each per country, the server keeping who has which), what it cost you, the dividend a data
 * centre pays every morning, your influence (the weight of what you've given), and what everyone gets from them:
 * a check-up, a lecture, a match, once a day each.
 */
import { earn, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localAmount } from './character.ts';

export type LegacyKindSim = 'tower' | 'hospital' | 'university' | 'stadium' | 'datacentre';
export interface Given { kind: LegacyKindSim; name: string; country: string; paid: number; /** its Lagos-terms cost */ lagos: number; at: number }
interface LegacyState { given: Given[]; perks: Record<string, number> }
const of = (s: LifeState): LegacyState => ((s as LifeState & { legacy?: LegacyState }).legacy ??= { given: [], perks: {} });
export const givenOf = (s: LifeState) => of(s).given;

/** what it costs here (a Lagos amount, at this country's money: not marked up like a shop price) */
export const legacyPrice = (s: LifeState, lagosCost: number) => localAmount(s, lagosCost, 1_000_000_000);

/** paid (after the server's said it's yours): the money goes, the build is yours, it's on your record */
export function giveLegacy(s: LifeState, kind: LegacyKindSim, name: string, lagosCost: number): { ok: boolean; line: string } {
  const price = legacyPrice(s, lagosCost);
  if (!spend(s, price, `Legacy: ${name}`)) return { ok: false, line: 'You don’t have enough for that.' };
  of(s).given.push({ kind, name, country: hereCountry(s), paid: price, lagos: lagosCost, at: s.minutes });
  s.history.push({ t: s.minutes, text: `Gave ${name}.` });
  s.needs.fun = Math.min(100, s.needs.fun + 30);
  return { ok: true, line: `${name} is yours: built, opened, your name on it. Everyone here can go.` };
}

/** your influence: what you've given, in billions (Lagos terms), and how many things */
export function influenceOf(s: LifeState): { points: number; count: number } {
  const g = of(s).given;
  return { points: Math.round(g.reduce((t, x) => t + (x.lagos ?? 0), 0) / 1e9), count: g.length };
}

/** every morning: each data centre you gave pays you 0.04% of what it cost */
export function legacyMorning(s: LifeState, events: LifeEvent[]) {
  void events;
  for (const g of of(s).given) if (g.kind === 'datacentre') earn(s, Math.round(g.paid * 0.0004), `Dividend: ${g.name}`);
}

/** what a build gives anyone who comes: once a day each */
export function legacyPerk(s: LifeState, kind: LegacyKindSim, name: string): string {
  const day = Math.floor(s.minutes / 1440), p = of(s).perks, n = s.needs, up = (k: keyof typeof n, v: number) => { n[k] = Math.max(0, Math.min(100, n[k] + v)); };
  if (p[kind] === day) return kind === 'datacentre' ? 'The tour’s done for today. The hum carries on without you.' : 'You’ve had that today. Come back tomorrow.';
  p[kind] = day;
  switch (kind) {
    case 'hospital': up('energy', 25); up('hygiene', 10); return `A nurse at ${name} takes your blood pressure, listens to your chest, looks in your ears. "You're in good shape. Drink more water." No bill. Nobody here gets one.`;
    case 'university': up('fun', 12); up('energy', -5); s.history.push({ t: s.minutes, text: `Sat in on a lecture at ${name}.` }); return `A lecture at ${name}: two hundred people in a tiered hall, a professor who makes economics sound like a thriller. You take notes. You understand more than you expected.`;
    case 'stadium': up('fun', 30); up('energy', -6); return `Match day at ${name}: sixty thousand on their feet, a last-minute winner, the whole place shaking. Your voice is gone.`;
    case 'datacentre': up('fun', 10); return `The tour of ${name}: badges, a cold aisle at 18 degrees, rows of blinking racks to the end of the hall, the roar of the fans. Half the country’s phones are talking to this room right now.`;
    default: up('fun', 15); return `The view from the top of ${name}.`;
  }
}
